import {
  useState,
  useCallback,
  ComponentType,
  ReactNode,
  PropsWithChildren,
  memo,
} from 'react';

import { PrimaryDropdownButton } from '@atlaskit/atlassian-navigation';
import { PopupMenuGroup, Section } from '@atlaskit/menu';
import { Popup } from '@atlaskit/popup';

import { AnalyticsEvent, MenuItem } from '../../types';

import { HorizontalNavLinkItem } from './horizontal-nav-link-item';

const PrimaryDropdownItem = ({
  menuItems,
  linkComponent,
  publishUiEvent,
  label,
  analyticsLabel,
  children,
}: {
  menuItems: MenuItem[];
  linkComponent?: ComponentType<PropsWithChildren<any>>;
  publishUiEvent: (event: AnalyticsEvent) => void;
  label: string;
  analyticsLabel: string;
  children?: ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const isHighlighted = menuItems.some(
    item => item.url === window.location.pathname
  );
  const triggerHandler = () => {
    setIsOpen(prev => !prev);
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'navigationItem',
      actionSubjectId: analyticsLabel,
      source: 'navigation',
      attributes: {
        itemIsGlobalHorizontalNav: true,
      },
    });
  };
  const renderMenu = useCallback(
    () => (
      <PopupMenuGroup minWidth={149}>
        {children}
        <Section hasSeparator>
          {menuItems.map(menuItem => (
            <HorizontalNavLinkItem
              linkComponent={linkComponent}
              key={menuItem.url}
              href={menuItem.url}
              isClientLink={menuItem.is_client_link}
              target={menuItem.target}
              analyticsLabel={menuItem.analytics_label}
              publishUiEvent={publishUiEvent}
              isSelected={menuItem.url === window.location.pathname}
            >
              {menuItem.label}
            </HorizontalNavLinkItem>
          ))}
        </Section>
      </PopupMenuGroup>
    ),
    [menuItems, linkComponent, publishUiEvent, children]
  );

  if (!menuItems.length) {
    return null;
  }

  return (
    <Popup
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      placement="bottom-start"
      trigger={triggerProps => (
        <PrimaryDropdownButton
          {...triggerProps}
          aria-label={label}
          isSelected={isOpen}
          isHighlighted={isHighlighted}
          onClick={triggerHandler}
        >
          {label}
        </PrimaryDropdownButton>
      )}
      content={renderMenu}
    />
  );
};

export default memo(PrimaryDropdownItem);
